import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { SectionLabel, Headline, Field, Stamp, PunchHoles } from "@/components/Doc";

export const PAPER = {
  title: "The Key Ring",
  subtitle: "How the modern company collapsed a dozen separate locks into a single key — and what the pre-computer office knew about keeping them apart.",
  form: "Form LH-30",
  rev: "Rev. 09/26",
  pages: 22,
  opening: "The threat is not that machines became clever. It is that your company stopped keeping its locks apart, and then cleverness got cheap.",
};

const contents = [
  ["I", "One key, every door", "What changed, precisely."],
  ["II", "Six cases, read closely", "A meeting that wasn't. One door, no bolt. A key never returned. An industry with no paper. And two that held."],
  ["III", "Seven controls that predate the computer", "Dual control. The callback. Separation of duties. The key register. The safe. The fallback. The written decision."],
  ["IV", "The human layer", "The techniques, briefly, and the single rule worth teaching if you only teach one."],
  ["V", "The Key Ring Audit", "Reading your score."],
  ["VI", "The ninety-day install", "Week one, nothing to buy. Month one, structure. Quarter one, rehearsal."],
  ["VII", "Sources & notes", "Incidents, statistics, controls and practice, regulatory."],
];

export const PaperCover = ({ compact = false }) => (
  <div className={`relative bg-manila border border-rule ${compact ? "p-6 pl-12" : "p-8 pl-14 sm:p-10 sm:pl-16"} shadow-[6px_6px_0_0_#1A1A18]`} data-testid="paper-cover">
    <PunchHoles />
    <div className={`absolute ${compact ? "top-12 right-5" : "top-16 right-8"}`}><Stamp color="red" rotate={6} size={compact ? "sm" : "md"}>Restricted</Stamp></div>
    <div className="flex justify-between t-label text-olive">
      <span>Longhand</span>
      <span>{PAPER.form}</span>
    </div>
    <h3 className={`t-head ${compact ? "text-4xl mt-8" : "text-5xl sm:text-6xl mt-12"} leading-none`}>{PAPER.title}</h3>
    <p className={`t-body ${compact ? "text-base mt-4" : "text-lg mt-6"} leading-snug max-w-[40ch]`}>{PAPER.subtitle}</p>
    <div className={`${compact ? "mt-8" : "mt-14"} border-t border-rule pt-3 flex justify-between t-label text-olive`}>
      <span>White paper · {PAPER.pages} pages</span>
      <span>{PAPER.rev}</span>
    </div>
  </div>
);

export const PaperGate = ({ compact = false }) => {
  const [f, setF] = useState({ name: "", email: "", company: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/paper/request", f);
      setDone(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div className="settle bg-paper border border-carbon p-7 sm:p-10 relative shadow-[6px_6px_0_0_#D9C9A8]" data-testid="paper-released">
      <div className="absolute -top-5 right-8"><Stamp color="red" rotate={-6} size="lg">Cleared</Stamp></div>
      <span className="t-label text-olive">{PAPER.form} — Released</span>
      <h2 className="t-head text-3xl mt-3">Your copy is ready.</h2>
      <p className="t-body text-lg mt-4 leading-[1.65]">Thank you, {f.name}. The paper opens below, and a link has been sent to {f.email}. The link is good for seven days.</p>
      <a href={done.download_url} target="_blank" rel="noreferrer" className="btn btn-stamp mt-8" data-testid="paper-download-btn">Open the Paper (PDF)</a>
    </div>
  );

  return (
    <form onSubmit={submit} className={`bg-paper border border-carbon ${compact ? "p-6" : "p-7 sm:p-10"} shadow-[6px_6px_0_0_#D9C9A8] flex flex-col gap-5`} data-testid="paper-gate-form">
      <div>
        <span className="t-label text-olive">Section 1 — Requestor</span>
        <h2 className="t-head text-2xl sm:text-3xl mt-2">Request the paper</h2>
      </div>
      <Field label="Name" name="name" value={f.name} onChange={set} required testId="paper-name" />
      <Field label="Work email" name="email" type="email" value={f.email} onChange={set} required testId="paper-email" />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Company" name="company" value={f.company} onChange={set} testId="paper-company" />
        <Field label="Role" name="role" value={f.role} onChange={set} testId="paper-role" />
      </div>
      <button type="submit" className="btn btn-stamp self-start mt-2" disabled={busy} data-testid="paper-submit-btn">{busy ? "Releasing…" : "Release the Paper"}</button>
      <p className="t-body italic text-base text-olive">We'll send the paper and nothing else. No sequence, no newsletter.</p>
    </form>
  );
};

export default function Paper() {
  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="paper-page">
      <SectionLabel right={PAPER.form}>Doc. 10 — The Paper</SectionLabel>
      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-6">
          <Headline as="h1" className="text-4xl sm:text-5xl lg:text-6xl">{PAPER.title}</Headline>
          <p className="t-body text-lg sm:text-xl mt-6 leading-[1.65] max-w-[52ch]">{PAPER.subtitle}</p>
          <blockquote className="t-body text-xl sm:text-2xl italic mt-10 pl-6 border-l-2 border-carbon max-w-[50ch] leading-snug">“{PAPER.opening}”</blockquote>
          <div className="mt-12">
            <SectionLabel>Contents</SectionLabel>
            <ol className="mt-6 flex flex-col">
              {contents.map(([n, h, sub]) => (
                <li key={n} className="grid grid-cols-[48px_1fr] gap-3 py-3 border-b border-rule/60" data-testid={`paper-contents-${n}`}>
                  <span className="t-label pt-1">{n}</span>
                  <div>
                    <p className="t-head text-xl leading-none">{h}</p>
                    <p className="t-body text-base text-olive mt-1">{sub}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 flex flex-col gap-10">
          <PaperCover />
          <PaperGate />
        </div>
      </div>
      <div className="mt-20 border-t border-carbon pt-12 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <h2 className="t-head text-3xl sm:text-4xl">Section V is the audit. Take it now.</h2>
        <Link to="/audit" className="btn" data-testid="paper-audit-btn">Take the Key Ring Audit</Link>
      </div>
    </main>
  );
}
